import { CellRendererSelectorFunc } from "@ag-grid-community/core";
import { HexType, ProjectRole } from "@hex/common";
import { identity } from "lodash";

import {
  MinimalCategory,
  MinimalStatus,
} from "../../../hooks/useProjectLabelsForHex.js";
import { getThreadTitle } from "../../agent/ask-from-home/hooks/useRecentThreads.js";
import { HexPermissionSummaryContainer } from "../../hex/HexPermissionSummaryContainer.js";
import { shouldShowViewPublishedAppStats } from "../../hex-list-2/hex-row/hooks/useShouldShowViewPublishedAppStats.js";
import { PermissionSummary } from "../../hex-list-2/hex-row/utils.js";
import { ProjectsTableRowData } from "../types.js";

import { EditableStatusTableCellRenderer } from "./table-cell-renderers/EditableStatusTableCellRenderer.js";
import { LinkedNameWithActionsMenuRenderer } from "./table-cell-renderers/LinkedNameWithActionsMenuRenderer.js";
import { ReadonlyNameCellRenderer } from "./table-cell-renderers/ReadonlyNameCellRenderer.js";
import { ThreadNameCellRenderer } from "./table-cell-renderers/ThreadNameCellRenderer.js";
import {
  AppViewsTableCellRenderer,
  CategoriesTableCellRenderer,
  CollapsedCategoriesTableCellRenderer,
  CollectionProjectRoleTableCellRenderer,
  NullishCellValue,
  StatusTableCellRenderer,
} from "./TableCellRenderers.js";

type HexCellRendererSelectorFunc<VALUE> = CellRendererSelectorFunc<
  ProjectsTableRowData,
  VALUE
>;

export const NameCellRendererSelector: CellRendererSelectorFunc<
  ProjectsTableRowData
> = ({ data }) => {
  if (data == null) {
    return {
      component: NullishCellValue,
    };
  }

  if (data.__typename === "UnknownHex") {
    return {
      component: ReadonlyNameCellRenderer,
      params: {
        mapDataToHex: identity,
      },
    };
  }

  if (data.hexType === HexType.ASK) {
    const threadId = data.latestAskAgentChatThread?.id;
    const threadTitle = getThreadTitle(
      data.title,
      data.latestAskAgentChatThread?.title ?? null,
      data.latestAskAgentChatThread?.latestUserMessage?.content ?? [],
    );

    return {
      component: ThreadNameCellRenderer,
      params: {
        mapDataToHex: identity,
        threadId,
        threadTitle,
      },
    };
  }

  return {
    component: LinkedNameWithActionsMenuRenderer,
    params: {
      mapDataToHex: identity,
    },
  };
};

export const AppViewsCellRendererSelector: HexCellRendererSelectorFunc<
  number
> = ({ data, value }) => {
  if (data == null || value == null || data.__typename === "UnknownHex") {
    return {
      component: NullishCellValue,
    };
  }

  const showViewPublishedAppStats = shouldShowViewPublishedAppStats(data);
  // Do not show to app-only viewers
  if (showViewPublishedAppStats && data.canViewLogic) {
    return {
      component: AppViewsTableCellRenderer,
      params: {
        id: data.id,
      },
    };
  }

  return {
    component: NullishCellValue,
  };
};

export const CategoriesCellRendererSelector: HexCellRendererSelectorFunc<
  MinimalCategory[]
> = ({ data, value: categories }) => {
  if (
    data == null ||
    categories == null ||
    !Array.isArray(categories) ||
    categories.length === 0
  ) {
    return {
      component: NullishCellValue,
    };
  }

  if (categories.length > 2) {
    return {
      component: CollapsedCategoriesTableCellRenderer,
    };
  }

  return {
    component: CategoriesTableCellRenderer,
  };
};

export const CollectionAccessCellRendererSelector: HexCellRendererSelectorFunc<
  ProjectRole
> = ({ context: { canEditCollection }, data }) => {
  if (
    data == null ||
    data.__typename === "UnknownHex" ||
    data.collectionHexLink == null
  ) {
    return {
      component: NullishCellValue,
    };
  }
  const {
    collectionHexLink: {
      collection: { id: collectionId },
      id: collectionHexLinkId,
    },
  } = data;

  return {
    component: CollectionProjectRoleTableCellRenderer,
    params: {
      canEditCollection,
      collectionId,
      hex: data,
      collectionHexLinkId,
    },
  };
};

export const PermissionSummaryCellRendererSelector: HexCellRendererSelectorFunc<
  PermissionSummary
> = ({ data, value }) => {
  if (data == null || value == null || data.__typename === "UnknownHex") {
    return {
      component: NullishCellValue,
    };
  }

  return {
    component: HexPermissionSummaryContainer,
    params: {
      icon: value.icon,
      minimal: undefined,
      text: value.text,
      tooltipText: value.tooltipText,
    },
  };
};

export const StatusCellRendererSelector: HexCellRendererSelectorFunc<
  MinimalStatus | null
> = ({ data, value }) => {
  if (data == null || data.__typename === "UnknownHex") {
    return {
      component: NullishCellValue,
    };
  }

  // If the user can edit metadata, show the editable status picker
  if (data.canEditMetadata) {
    return {
      component: EditableStatusTableCellRenderer,
      params: {
        hexId: data.id,
      },
    };
  }

  // Otherwise, show the read-only status label
  if (value == null) {
    return {
      component: NullishCellValue,
    };
  }

  return {
    component: StatusTableCellRenderer,
  };
};
